import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import api from "../../utils/api";
import styles from "./NewOrgConnectionPage.module.scss";

interface Organization {
  value: string; // id
  label: string; // name
}

const NewOrgConnectionPage: React.FC = () => {
  const [orgOne, setOrgOne] = useState<Organization | null>(null);
  const [orgTwo, setOrgTwo] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  const searchOrganizations = async (inputValue: string) => {
    if (!inputValue || inputValue.length < 2) {
      console.log("[searchOrganizations] Input too short:", inputValue);
      return [];
    }
    try {
      const res = await api.get<{ data: { id: string; name: string }[] }>(
        `/core/organization/search?query=${encodeURIComponent(inputValue)}`
      );
      console.log("[searchOrganizations] Query:", inputValue, "Response:", res.data);
      return res.data.data.map((org) => ({
        value: org.id,
        label: org.name,
      }));
    } catch (err: any) {
      console.error("[searchOrganizations] Error:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to search organizations");
      return [];
    }
  };

  const handleSubmit = async () => {
    if (!orgOne || !orgTwo) {
      setError("Please select both organizations");
      return;
    }
    if (orgOne.value === orgTwo.value) {
      setError("Cannot connect an organization to itself");
      return;
    }
    try {
      setError("");
      setLoading(true);
      await api.post("/core/connections/org/request", {
        org_id_one: orgOne.value,
        org_id_two: orgTwo.value,
      });
      navigate("/connections");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to create connection";
      console.error("[handleSubmit] Error:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <h2 className={styles.title}>New Organization Connection</h2>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="orgOne">Organization One</label>
              <AsyncSelect
                id="orgOne"
                placeholder="Search for an organization..."
                loadOptions={searchOrganizations}
                onChange={(option) => setOrgOne(option)}
                value={orgOne}
                isSearchable
                cacheOptions
                defaultOptions={[]}
                className={styles.select}
                classNamePrefix="react-select"
                noOptionsMessage={() => "No organizations found"}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="orgTwo">Organization Two</label>
              <AsyncSelect
                id="orgTwo"
                placeholder="Search for an organization..."
                loadOptions={searchOrganizations}
                onChange={(option) => setOrgTwo(option)}
                value={orgTwo}
                isSearchable
                cacheOptions
                defaultOptions={[]}
                className={styles.select}
                classNamePrefix="react-select"
                noOptionsMessage={() => "No organizations found"}
              />
            </div>
            <div className={styles.actions}>
              <button onClick={handleSubmit} className={styles.submitButton} disabled={loading}>
                Create Connection
              </button>
              <button onClick={() => navigate("/connections")} className={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewOrgConnectionPage;
